/**
 * LobbyScreen — Full-screen overlay for the BR `waiting` phase. Shows the
 * live player roster, lobby countdown, and a LEAVE button.
 */
import { MAX_PLAYERS_PER_ROOM, BR_LOBBY_MIN_PLAYERS } from '../../shared/constants.js';
import { formatMMSS } from './format.js';

const MAX_PLAYER_ROWS = MAX_PLAYERS_PER_ROOM;

interface PlayerRowCache {
  row: HTMLElement;
  name: HTMLElement;
  cachedName: string;
  cachedClass: string;
}

export class LobbyScreen {
  private overlay: HTMLElement;
  private countEl: HTMLElement;
  private statusEl: HTMLElement;
  private listEl: HTMLElement;
  private leaveBtn: HTMLButtonElement;
  private rowCache: PlayerRowCache[] = [];
  private onLeaveCb: (() => void) | null = null;
  private visible = false;
  private lastCount = '';
  private lastStatus = '';

  constructor(parentElement: HTMLElement) {
    this.overlay = document.createElement('div');
    this.overlay.className = 'lobby-overlay hidden';

    const card = document.createElement('div');
    card.className = 'lobby-card';

    const title = document.createElement('div');
    title.className = 'lobby-title';
    title.textContent = 'BATTLE ROYALE';
    card.appendChild(title);

    const subtitle = document.createElement('div');
    subtitle.className = 'lobby-subtitle';
    subtitle.textContent = 'Awaiting Contestants';
    card.appendChild(subtitle);

    this.countEl = document.createElement('div');
    this.countEl.className = 'lobby-count';
    this.countEl.textContent = `0 / ${MAX_PLAYER_ROWS}`;
    card.appendChild(this.countEl);

    this.statusEl = document.createElement('div');
    this.statusEl.className = 'lobby-status';
    this.statusEl.textContent = 'Waiting for opponents...';
    card.appendChild(this.statusEl);

    this.listEl = document.createElement('div');
    this.listEl.className = 'lobby-playerlist';
    for (let i = 0; i < MAX_PLAYER_ROWS; i++) {
      const row = document.createElement('div');
      row.className = 'lobby-player-row';
      row.style.display = 'none';

      const name = document.createElement('span');
      name.className = 'lobby-player-name';
      row.appendChild(name);

      this.listEl.appendChild(row);
      this.rowCache.push({ row, name, cachedName: '', cachedClass: '' });
    }
    card.appendChild(this.listEl);

    this.leaveBtn = document.createElement('button');
    this.leaveBtn.type = 'button';
    this.leaveBtn.className = 'lobby-leave-btn';
    this.leaveBtn.textContent = 'LEAVE LOBBY';
    this.leaveBtn.addEventListener('click', () => this.onLeaveCb?.());
    card.appendChild(this.leaveBtn);

    this.overlay.appendChild(card);
    parentElement.appendChild(this.overlay);
  }

  show(onLeave: () => void): void {
    this.onLeaveCb = onLeave;
    this.overlay.classList.remove('hidden');
    this.visible = true;
  }

  hide(): void {
    this.overlay.classList.add('hidden');
    this.visible = false;
  }

  isVisible(): boolean {
    return this.visible;
  }

  update(state: any, localSessionId: string): void {
    if (!this.visible) return;

    const players = state?.players;
    const entries: Array<{ sessionId: string; username: string }> = [];
    players?.forEach?.((p: any) => {
      entries.push({
        sessionId: p.sessionId ?? '',
        username: p.username ?? 'anon',
      });
    });

    const count = entries.length;
    const countText = `${count} / ${MAX_PLAYER_ROWS}`;
    if (countText !== this.lastCount) {
      this.countEl.textContent = countText;
      this.lastCount = countText;
    }

    const countdown = Number(state?.lobbyCountdown ?? 0);
    let statusText: string;
    if (count < BR_LOBBY_MIN_PLAYERS) {
      statusText = 'Waiting for opponents...';
    } else if (countdown > 0) {
      statusText = `Match starting in ${formatMMSS(Math.ceil(countdown))}`;
    } else {
      statusText = 'Preparing...';
    }
    if (statusText !== this.lastStatus) {
      this.statusEl.textContent = statusText;
      this.lastStatus = statusText;
    }

    for (let i = 0; i < MAX_PLAYER_ROWS; i++) {
      const cache = this.rowCache[i];
      if (i < entries.length) {
        const e = entries[i];
        const className = 'lobby-player-row' + (e.sessionId === localSessionId ? ' local' : '');
        if (cache.row.style.display === 'none') cache.row.style.display = '';
        if (className !== cache.cachedClass) {
          cache.row.className = className;
          cache.cachedClass = className;
        }
        if (e.username !== cache.cachedName) {
          cache.name.textContent = e.username;
          cache.cachedName = e.username;
        }
      } else if (cache.row.style.display !== 'none') {
        cache.row.style.display = 'none';
      }
    }
  }
}
